// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import {
  ApproveApprovableChange,
  BlockApprovableChange,
  DeferApprovableChange,
  FetchApprovableChangeSchemas,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { getSchema } from "@utils/schema";

export type ApprovableChangeRequest = $Exact<{
  clientId: string,
  changesetId: string,
  notes?: Object,
  successRedirect?: string,
  successCallback?: Function,
}>;

export function* onApproveApprovableChange({
  payload,
}: Action<ApprovableChangeRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.changesetId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/change-set/${payload.changesetId}/approve`,
    );

    yield all([
      put(actionCreators.ApproveApprovableChangeSuccess()),
      put(actionCreators.ToggleApproveApprovableChangeModal(false)),
    ]);

    if (payload.successRedirect) {
      yield put(push(payload.successRedirect));
    }

    if (payload.successCallback) {
      yield put(payload.successCallback());
    }
    yield put(sendSuccessMessage("PENDING_REVIEW_APPROVED"));
  } catch (e) {
    yield put(actionCreators.ApproveApprovableChangeError(e.response));
  }
}

export function* onBlockApprovableChange({
  payload,
}: Action<ApprovableChangeRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.changesetId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/change-set/${payload.changesetId}/block`,
      payload.notes,
    );

    yield all([
      put(actionCreators.BlockApprovableChangeSuccess()),
      put(actionCreators.ToggleBlockApprovableChangeModal(false)),
    ]);

    if (payload.successRedirect) {
      yield put(push(payload.successRedirect));
    }

    if (payload.successCallback) {
      yield put(payload.successCallback());
    }
    yield put(sendSuccessMessage("ALERT_ATTACHMENT_BLOCKED"));
  } catch (e) {
    yield put(actionCreators.BlockApprovableChangeError(e.response));
  }
}

export function* onDeferApprovableChange({
  payload,
}: Action<ApprovableChangeRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.changesetId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/change-set/${payload.changesetId}/defer`,
      payload.notes,
    );

    yield all([
      put(actionCreators.DeferApprovableChangeSuccess()),
      put(actionCreators.ToggleDeferApprovableChangeModal(false)),
    ]);

    if (payload.successRedirect) {
      yield put(push(payload.successRedirect));
    }

    if (payload.successCallback) {
      yield put(payload.successCallback());
    }
    yield put(sendSuccessMessage("PENDING_REVIEW_DEFFERED"));
  } catch (e) {
    yield put(actionCreators.DeferApprovableChangeError(e.response));
  }
}

export function* onFetchApprovableChangeSchemas(): Generator<*, void, void> {
  yield put(
    actionCreators.SetApprovableChangeSchemas({
      block: getSchema("BlockChangeSetNotesDTO"),
      defer: getSchema("DeferChangeSetNotesDTO"),
      approve: getSchema("ChangeSetDTO"),
    }),
  );
}

// ROOT

export function* ApprovableChangeSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(ApproveApprovableChange, onApproveApprovableChange),
    takeEvery(BlockApprovableChange, onBlockApprovableChange),
    takeEvery(DeferApprovableChange, onDeferApprovableChange),
    takeEvery(FetchApprovableChangeSchemas, onFetchApprovableChangeSchemas),
  ]);
}

export default ApprovableChangeSagas;
